// React Query хуки для карточки проекта.
import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import {
  fetchProjectAccess,
  fetchProjectCardSummary,
  fetchProjectCardTasks,
  fetchProjectCardTasksPage,
  fetchProjectCharts,
  fetchProjectData,
  fetchProjectEmployeeHours,
  fetchProjectResults,
  fetchProjectTeam,
  fetchProjectTimeline,
  type ProjectTasksQuery,
} from './api/project-card';
import {
  fetchProjectFields,
  fetchProjectStages,
  fetchProjectsAnalytics,
  fetchProjectsList,
  type ProjectsFilterParams,
  type ProjectsListParams,
} from './api/projects-list';

const STALE = 60 * 1000;

/**
 * Справочник стадий воронки (parity §3.2). Без аргумента — все типы портала.
 * Меняется редко — держим 10 минут.
 */
export const useProjectStages = (entityTypeIds?: number[]) =>
  useQuery({
    queryKey: [
      'project-stages',
      entityTypeIds?.slice().sort().join(',') ?? 'all',
    ],
    queryFn: () => fetchProjectStages(entityTypeIds),
    staleTime: 10 * 60 * 1000,
  });

/**
 * Схема полей карточки проекта — по ней строится форма редактирования (включая
 * ещё не заполненные поля). Схема берётся из кэша метаданных бэка, который
 * наполняется один раз при старте, — поле, заведённое в Битриксе только что,
 * появится здесь после рестарта бэкенда. Меняется редко, держим 10 минут.
 */
export const useProjectFields = (entityTypeId: number) =>
  useQuery({
    queryKey: ['project-fields', entityTypeId],
    queryFn: () => fetchProjectFields(entityTypeId),
    enabled: Number.isFinite(entityTypeId) && entityTypeId > 0,
    staleTime: 10 * 60 * 1000,
  });

export const useProjectCardSummary = (entityTypeId: number, itemId: number) =>
  useQuery({
    queryKey: ['project-card', entityTypeId, itemId, 'summary'],
    queryFn: () => fetchProjectCardSummary(entityTypeId, itemId),
    enabled: Number.isFinite(entityTypeId) && Number.isFinite(itemId),
    staleTime: STALE,
  });

export const useProjectTeam = (entityTypeId: number, itemId: number) =>
  useQuery({
    queryKey: ['project-card', entityTypeId, itemId, 'team'],
    queryFn: () => fetchProjectTeam(entityTypeId, itemId),
    enabled: Number.isFinite(entityTypeId) && Number.isFinite(itemId),
    staleTime: STALE,
  });

// enabled — ленивая загрузка: запрос идёт только когда вкладка «Результаты»
// открыта (см. useProjectDetail), а не на маунте детальной страницы.
export const useProjectResults = (
  entityTypeId: number,
  itemId: number,
  enabled = true
) =>
  useQuery({
    queryKey: ['project-card', entityTypeId, itemId, 'results'],
    queryFn: () => fetchProjectResults(entityTypeId, itemId),
    enabled:
      enabled && Number.isFinite(entityTypeId) && Number.isFinite(itemId),
    staleTime: STALE,
  });

export const useProjectData = (
  entityTypeId: number,
  itemId: number,
  source: 'smart' | 'task' | 'all' = 'all',
  enabled = true
) =>
  useQuery({
    queryKey: ['project-card', entityTypeId, itemId, 'data', source],
    queryFn: () => fetchProjectData(entityTypeId, itemId, source),
    enabled:
      enabled && Number.isFinite(entityTypeId) && Number.isFinite(itemId),
    staleTime: STALE,
  });

// Курсорная пагинация таймлайна (?cursor=<date> из nextCursor).
export const useProjectTimelineInfinite = (
  entityTypeId: number,
  itemId: number,
  enabled = true
) =>
  useInfiniteQuery({
    queryKey: ['project-card', entityTypeId, itemId, 'timeline-inf'],
    queryFn: ({ pageParam }) =>
      fetchProjectTimeline(entityTypeId, itemId, {
        cursor: pageParam as string | undefined,
        limit: 10,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor || undefined,
    enabled:
      enabled && Number.isFinite(entityTypeId) && Number.isFinite(itemId),
    staleTime: STALE,
  });

export const useProjectAccess = (entityTypeId: number, itemId: number) =>
  useQuery({
    queryKey: ['project-card', entityTypeId, itemId, 'access'],
    queryFn: () => fetchProjectAccess(entityTypeId, itemId),
    enabled: Number.isFinite(entityTypeId) && Number.isFinite(itemId),
    staleTime: STALE,
  });

// Задачи проекта (для таба «Задачи»).
export const useProjectCardTasks = (
  entityTypeId: number,
  itemId: number,
  enabled = true
) =>
  useQuery({
    queryKey: ['project-card', entityTypeId, itemId, 'tasks'],
    queryFn: () => fetchProjectCardTasks(entityTypeId, itemId),
    enabled:
      enabled && Number.isFinite(entityTypeId) && Number.isFinite(itemId),
    staleTime: STALE,
  });

// Пагинированный список задач (infinite scroll). Фильтры (search/dept/status…)
// в queryKey → их смена создаёт новый запрос с page=1. view=card на бэке.
export const useProjectCardTasksInfinite = (
  entityTypeId: number,
  itemId: number,
  query: ProjectTasksQuery,
  enabled = true,
  limit = 20
) =>
  useInfiniteQuery({
    queryKey: ['project-card', entityTypeId, itemId, 'tasks-inf', limit, query],
    queryFn: ({ pageParam }) =>
      fetchProjectCardTasksPage(entityTypeId, itemId, {
        ...query,
        page: pageParam as number,
        limit,
      }),
    initialPageParam: 1,
    getNextPageParam: (last, pages) =>
      last.hasMore ? pages.length + 1 : undefined,
    // Не мигаем лоадером при смене фильтра — держим прошлый список до новых данных.
    placeholderData: keepPreviousData,
    enabled:
      enabled && Number.isFinite(entityTypeId) && Number.isFinite(itemId),
    staleTime: STALE,
  });

// Диаграмма «Часы по сотрудникам» (план/факт) — вкладка «Задачи». Не зависит
// от фильтров/пагинации списка задач, грузится один раз при открытии таба.
export const useProjectEmployeeHours = (
  entityTypeId: number,
  itemId: number,
  enabled = true
) =>
  useQuery({
    queryKey: ['project-card', entityTypeId, itemId, 'employee-hours'],
    queryFn: () => fetchProjectEmployeeHours(entityTypeId, itemId),
    enabled:
      enabled && Number.isFinite(entityTypeId) && Number.isFinite(itemId),
    staleTime: STALE,
  });

// Диаграммы карточки проекта (GET /charts?days=N) — серверная агрегация по 5
// статусам (counts) и часам, per-исполнитель и per-отдел, за окно дней.
export const useProjectCharts = (
  entityTypeId: number,
  itemId: number,
  days: number,
  enabled = true
) =>
  useQuery({
    queryKey: ['project-card', entityTypeId, itemId, 'charts', days],
    queryFn: () => fetchProjectCharts(entityTypeId, itemId, days),
    enabled:
      enabled && Number.isFinite(entityTypeId) && Number.isFinite(itemId),
    staleTime: STALE,
  });

// Список проектов (GET /projects/list) — заменяет сборку из /tasks.
export const useProjectsList = (params?: ProjectsListParams, enabled = true) =>
  useQuery({
    queryKey: ['projects-list', params],
    queryFn: () => fetchProjectsList(params),
    placeholderData: keepPreviousData,
    enabled,
    staleTime: STALE,
  });

// Аналитика по исполнителям (GET /projects/analytics) — для двух верхних диаграмм.
// Передаём те же фильтры, что и в useProjectsList, чтобы наборы совпадали.
export const useProjectsAnalytics = (params?: ProjectsFilterParams) =>
  useQuery({
    queryKey: ['projects-analytics', params],
    queryFn: () => fetchProjectsAnalytics(params),
    staleTime: STALE,
  });
